import React, { useState } from 'react';
import { string, arrayOf, shape } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { isOriginInUse } from '../../util/search';
import { stringify } from '../../util/urlHelpers';
import { LocationAutocompleteInput, NamedLink } from '../../components';
import css from './SectionHeroBranded.module.css';

const SPECIALTIES = [
  'Personal Training',
  'HIIT',
  'Yoga',
  'Pilates',
  'Strength Training',
  'Boxing',
  'CrossFit',
  'Calisthenics',
  'Nutrition Coaching',
  'Group Fitness',
];

const identity = v => v;

const HeroSearch = () => {
  const [locationValue, setLocationValue] = useState({ search: '', selectedPlace: null });
  const [specialty, setSpecialty] = useState('');
  const history = useHistory();
  const config = useConfiguration();

  const handleSubmit = e => {
    e.preventDefault();
    const params = {};

    if (locationValue.selectedPlace) {
      params.address = locationValue.search;
      params.bounds = locationValue.selectedPlace.bounds;
      if (isOriginInUse(config) && locationValue.selectedPlace.origin) {
        params.origin = locationValue.selectedPlace.origin;
      }
    }

    if (specialty) params.keywords = specialty;

    const queryStr = stringify(params);
    history.push(`/s${queryStr ? `?${queryStr}` : ''}`);
  };

  const locationInput = {
    name: 'hero-location',
    value: locationValue,
    onChange: setLocationValue,
    onBlur: () => {},
    onFocus: () => {},
  };

  return (
    <form className={css.searchForm} onSubmit={handleSubmit} role="search" aria-label="Find a trainer">
      <div className={css.searchFields}>
        <div className={css.searchField}>
          <label className={css.searchLabel}>Location</label>
          <LocationAutocompleteInput
            rootClassName={css.locationRoot}
            iconClassName={css.locationIcon}
            inputClassName={css.searchInput}
            predictionsClassName={css.locationPredictions}
            placeholder="City or zip code"
            closeOnBlur
            format={identity}
            input={locationInput}
            meta={{ valid: true, touched: false }}
            hideErrorMessage
          />
        </div>
        <div className={css.searchDivider} aria-hidden="true" />
        <div className={css.searchField}>
          <label className={css.searchLabel} htmlFor="hero-specialty">Specialty</label>
          <select
            id="hero-specialty"
            className={css.searchSelect}
            value={specialty}
            onChange={e => setSpecialty(e.target.value)}
          >
            <option value="">Any specialty</option>
            {SPECIALTIES.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <button type="submit" className={css.searchButton} aria-label="Search trainers">
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        Search
      </button>
    </form>
  );
};

const SectionHeroBranded = props => {
  const {
    title,
    subtitle,
    description,
    primaryButtonText,
    primaryButtonLink,
    secondaryButtonText,
    secondaryButtonLink,
    trustBadges,
  } = props;

  const defaultBadges = [
    { text: 'All Trainers ID Verified' },
    { text: '5/5 Average Rating' },
    { text: 'Secure Payment' },
  ];

  const badges = trustBadges || defaultBadges;

  return (
    <section className={css.root}>
      <div className={css.overlay} />
      <div className={css.container}>
        {/* Trust Badges */}
        <div className={css.trustBadges}>
          {badges.map((badge, index) => (
            <div key={index} className={css.badge}>
              <svg aria-hidden="true" className={css.badgeIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className={css.badgeText}>{badge.text}</span>
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className={css.content}>
          <h1 className={css.title}>
            {title || (
              <FormattedMessage id="SectionHeroBranded.title" />
            )}
          </h1>

          {subtitle && <p className={css.subtitle}>{subtitle}</p>}

          <HeroSearch />

          {description && <p className={css.description}>{description}</p>}

          {/* CTA Buttons */}
          <div className={css.ctaContainer}>
            {primaryButtonLink && (
              <>
                {primaryButtonLink.startsWith('/') ? (
                  <a href={primaryButtonLink} className={css.btnPrimary}>
                    {primaryButtonText || 'Find Your Trainer Now →'}
                  </a>
                ) : (
                  <NamedLink name={primaryButtonLink} className={css.btnPrimary}>
                    {primaryButtonText || 'Find Your Trainer Now →'}
                  </NamedLink>
                )}
              </>
            )}

            {secondaryButtonLink && (
              <>
                {secondaryButtonLink.startsWith('/') || secondaryButtonLink.startsWith('#') ? (
                  <a href={secondaryButtonLink} className={css.btnSecondary}>
                    {secondaryButtonText || 'See How It Works'}
                  </a>
                ) : (
                  <NamedLink name={secondaryButtonLink} className={css.btnSecondary}>
                    {secondaryButtonText || 'See How It Works'}
                  </NamedLink>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

SectionHeroBranded.defaultProps = {
  title: 'Find Your Perfect Personal Trainer!',
  subtitle: 'Train in-person at vetted facilities or virtually from home',
  description: 'No membership fees · Pay-as-you-go · Cancel anytime',
  primaryButtonText: 'Find Your Trainer Now →',
  primaryButtonLink: 'SearchPage',
  secondaryButtonText: 'See How It Works',
  secondaryButtonLink: null,
  trustBadges: null,
};

SectionHeroBranded.propTypes = {
  title: string,
  subtitle: string,
  description: string,
  primaryButtonText: string,
  primaryButtonLink: string,
  secondaryButtonText: string,
  secondaryButtonLink: string,
  trustBadges: arrayOf(
    shape({
      text: string.isRequired,
    })
  ),
};

export default SectionHeroBranded;